import { systemModelsAPI } from '@/utils/api';
import { normalizePersistedImageUrl, uploadsPathFromSignedViewUrl } from '@/utils/imageUrlUtils';
import {
  buildStaticTryOnPresets,
  findTryOnPresetById,
  isTryOnSystemPresetAssetUrl,
  labelForTryOnPresetEthnicity,
  MODEL_TRY_ON_PRESETS_PER_COMBO,
  MODEL_TRY_ON_PRESET_DISK_EXT,
  MODEL_TRY_ON_TRIPLE_IMPORT_SLOTS,
  preferTryOnPresetWebpUrl,
  resolveTryOnPresetSubmitUrl,
} from '@/pages/ipe/workbench/constants/modelTryOnPresetConfig';
import { isGarmentTypeAllowedForModel } from '@/pages/ipe/workbench/constants/modelStudioConfig';
import { blockBareManagedSystemModelImageSrc } from '@/pages/ipe/workbench/utils/systemModelPresetNetworkSecurity';

/** 节点 11.7：用户端试衣 preset 数据来源 */
export const TRY_ON_PRESET_SOURCE = {
  API: 'api',
  STATIC: 'static',
  EMPTY: 'empty',
};

/** 节点 12.2：modelType + garmentType 组合键，用于联动刷新与选中校验 */
export function buildTryOnPresetComboKey(modelType, garmentType) {
  const model = String(modelType || '').trim();
  const garment = String(garmentType || '').trim();
  if (!model || !garment) return '';
  return `${model}|${garment}`;
}

/** presetId 是否属于当前 modelType + garmentType 组合（如 female-hoodie-1） */
export function isTryOnPresetIdForCombo(presetId, modelType, garmentType) {
  const id = String(presetId || '').trim();
  if (!id) return false;
  const prefix = `${String(modelType || '').trim()}-${String(garmentType || '').trim()}-`;
  return Boolean(prefix.length > 2) && id.startsWith(prefix);
}

/** 对齐后端 SystemModelStudioAlignment.buildLegacyPresetId */
export function buildLegacyTryOnPresetId(modelType, garmentType, poseIndex) {
  const model = String(modelType || '').trim();
  const garment = String(garmentType || '').trim();
  const index = Number(poseIndex);
  if (!model || !garment || !Number.isFinite(index) || index < 1) return '';
  return `${model}-${garment}-${index}`;
}

/**
 * 节点 12.2：presets 加载完成后解析应选中的 presetId。
 * 当前列表已按 modelType+garmentType 过滤；选中态以列表成员为准（兼容 sm-* 与 legacy id）。
 */
export function resolveNextTryOnPresetSelection({
  presets = [],
  selectedPresetId = '',
  modelType,
  garmentType,
  allowAutoSelect = true,
} = {}) {
  const selectable = filterSelectableTryOnPresets(presets);
  if (selectable.length === 0) return '';
  const id = String(selectedPresetId || '').trim();
  if (id) {
    const hit = findTryOnPresetInList(selectable, id);
    if (hit) return String(hit.id || id);
    // 无 apiId 映射时的静态 legacy 前缀兜底
    if (
      isTryOnPresetIdForCombo(id, modelType, garmentType)
      && selectable.some((item) => String(item?.id) === id)
    ) {
      return id;
    }
  }
  if (!allowAutoSelect) return '';
  return String(selectable[0]?.id || '');
}

/** 节点 12.4：系统模特持久化 URL（优先 uploads；签发地址还原 canonical；静态 assets 用磁盘扩展名） */
export function resolveTryOnPresetPersistedUrl(preset) {
  if (!preset) return '';
  const apiUrl = String(preset.apiUrl || '').trim();
  if (apiUrl) {
    if (apiUrl.includes('/api/storage/images/view') || apiUrl.includes('/api/storage/view')) {
      const fromSigned = uploadsPathFromSignedViewUrl(apiUrl);
      if (fromSigned) return fromSigned;
    }
    return resolveTryOnPresetSubmitUrl({ ...preset, apiUrl, url: apiUrl });
  }
  const candidates = [preset.url, preset.displayUrl, preset.viewUrl];
  for (const candidate of candidates) {
    const fromSigned = uploadsPathFromSignedViewUrl(candidate);
    if (fromSigned) return fromSigned;
    const persisted = normalizePersistedImageUrl(candidate);
    if (persisted) return persisted;
    const raw = String(candidate || '').trim();
    if (!raw) continue;
    if (raw.includes('/api/storage/images/view') || raw.includes('/api/storage/view')) continue;
    // 静态 assets：提交/持久化须用磁盘扩展名（非展示 webp）
    if (isTryOnSystemPresetAssetUrl(raw) || raw.startsWith('/assets/model-try-on-presets/')) {
      return resolveTryOnPresetSubmitUrl({ ...preset, url: raw, apiUrl: '' });
    }
    if (raw.startsWith('/uploads/')) return raw;
  }
  return '';
}

/**
 * 提交试衣/手持/穿戴 API 用的系统模特图地址：
 * 优先 managed `/uploads/`（Worker 可读 OSS），勿用展示用 webp 或仅前端 CDN 可访问的静态路径。
 */
export function resolveTryOnPresetImageUrlForApi(preset) {
  if (!preset) return '';
  const apiUrl = String(preset.apiUrl || '').trim();
  if (apiUrl.startsWith('/uploads/') || apiUrl.startsWith('uploads/')) {
    return apiUrl.startsWith('/') ? apiUrl : `/${apiUrl}`;
  }
  const fromSigned = uploadsPathFromSignedViewUrl(apiUrl)
    || uploadsPathFromSignedViewUrl(preset.displayUrl)
    || uploadsPathFromSignedViewUrl(preset.viewUrl)
    || uploadsPathFromSignedViewUrl(preset.url);
  if (fromSigned) return fromSigned;
  const preferred = resolveTryOnPresetPersistedUrl(preset);
  if (preferred && !isTryOnSystemPresetAssetUrl(preferred)) {
    return preferred;
  }
  if (preferred) return preferred;
  return resolveTryOnPresetSubmitUrl(preset);
}

/** 节点 12.4：选中系统模特时写入的任务参数字段 */
export function buildTryOnSystemModelSelectionParams(preset) {
  const id = String(preset?.id || '').trim();
  if (!id || preset?.isEmpty) {
    return {
      usedUploadedModel: false,
      selectedPresetId: '',
      selectedPresetUrl: '',
      selectedPresetDisplayUrl: '',
      modelImageUrl: '',
      modelImagePreviewUrl: '',
    };
  }
  const signedDisplay = String(preset.displayUrl || preset.viewUrl || '').trim();
  return {
    usedUploadedModel: false,
    selectedPresetId: id,
    selectedPresetUrl: resolveTryOnPresetPersistedUrl(preset),
    selectedPresetDisplayUrl: signedDisplay,
    modelImageUrl: '',
    modelImagePreviewUrl: signedDisplay,
  };
}

export function buildTryOnUploadedModelSelectionParams({
  modelImageUrl = '',
  modelImagePreviewUrl = '',
} = {}) {
  return {
    usedUploadedModel: true,
    selectedPresetId: '',
    selectedPresetUrl: '',
    selectedPresetDisplayUrl: '',
    modelImageUrl: String(modelImageUrl || '').trim(),
    modelImagePreviewUrl: String(modelImagePreviewUrl || modelImageUrl || '').trim(),
  };
}

/** 节点 12.6：用户上传自定义模特后暂停系统模特选择器 */
export function isTryOnSystemPresetSelectorPaused({ hasUploadedModel, usedUploadedModel } = {}) {
  if (hasUploadedModel === true) return true;
  return usedUploadedModel === true;
}

/** 节点 12.4：试衣任务 initialResult / 工作流参数中的模特来源字段 */
export function buildTryOnTaskModelParams({ uploadedModelRef, systemPreset } = {}) {
  if (uploadedModelRef) {
    return {
      ...buildTryOnUploadedModelSelectionParams(),
      hasCustomModel: true,
    };
  }
  if (systemPreset?.id) {
    return {
      ...buildTryOnSystemModelSelectionParams(systemPreset),
      hasCustomModel: true,
    };
  }
  return {
    ...buildTryOnSystemModelSelectionParams(null),
    hasCustomModel: false,
  };
}

export function resolveTryOnPresetSelectionFields({
  presets = [],
  selectedPresetId = '',
  modelType,
  garmentType,
  allowAutoSelect = true,
} = {}) {
  const nextPresetId = resolveNextTryOnPresetSelection({
    presets,
    selectedPresetId,
    modelType,
    garmentType,
    allowAutoSelect,
  });
  if (!nextPresetId) {
    return buildTryOnSystemModelSelectionParams(null);
  }
  const preset = findTryOnPresetInList(presets, nextPresetId)
    || presets.find((item) => String(item?.id) === nextPresetId);
  return buildTryOnSystemModelSelectionParams(preset || { id: nextPresetId });
}

/** 节点 12.3：从 preset 解析 poseIndex 槽位（1–3） */
export function resolveTryOnPresetSlotIndex(preset) {
  const index = Number(preset?.index ?? preset?.poseIndex);
  if (Number.isFinite(index) && index >= 1 && index <= MODEL_TRY_ON_PRESETS_PER_COMBO) {
    return index;
  }
  const match = String(preset?.id || '').match(/-(\d+)$/);
  if (match) {
    const parsed = Number(match[1]);
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= MODEL_TRY_ON_PRESETS_PER_COMBO) {
      return parsed;
    }
  }
  return 0;
}

/** 节点 12.3：按 poseIndex 升序，同槽位保留首条 */
export function sortTryOnPresetsByPoseIndex(presets = []) {
  if (!Array.isArray(presets)) return [];
  return [...presets].sort((left, right) => {
    const leftIndex = resolveTryOnPresetSlotIndex(left) || 999;
    const rightIndex = resolveTryOnPresetSlotIndex(right) || 999;
    if (leftIndex !== rightIndex) return leftIndex - rightIndex;
    return String(left?.id || '').localeCompare(String(right?.id || ''));
  });
}

export function filterSelectableTryOnPresets(presets = []) {
  if (!Array.isArray(presets)) return [];
  return presets.filter((preset) => !preset?.isEmpty && String(preset?.id || '').trim());
}

/**
 * 节点 12.3：试衣三宫格展示槽位 — 固定 3 个 ethnicity 槽（poseIndex 1–3），最多展示 3 张。
 * 无素材的槽位保留空态卡片（不可选）。
 */
export function buildTryOnPresetDisplaySlots(modelType, garmentType, presets = []) {
  const model = String(modelType || '').trim();
  const garment = String(garmentType || '').trim();
  if (!model || !garment) return [];

  const bySlotIndex = new Map();
  sortTryOnPresetsByPoseIndex(presets)
    .slice(0, MODEL_TRY_ON_PRESETS_PER_COMBO)
    .forEach((preset) => {
      const slotIndex = resolveTryOnPresetSlotIndex(preset);
      if (slotIndex > 0 && !bySlotIndex.has(slotIndex)) {
        bySlotIndex.set(slotIndex, preset);
      }
    });

  const slots = [];
  for (let index = 1; index <= MODEL_TRY_ON_PRESETS_PER_COMBO; index += 1) {
    const slotMeta = MODEL_TRY_ON_TRIPLE_IMPORT_SLOTS.find((entry) => entry.index === index);
    const preset = bySlotIndex.get(index);
    const ethnicityLabel = labelForTryOnPresetEthnicity(
      preset?.ethnicity || slotMeta?.ethnicity,
      index,
    );

    if (preset) {
      slots.push({
        ...preset,
        slotIndex: index,
        presetIndex: index - 1,
        ethnicityLabel,
        isEmpty: false,
        selectable: true,
      });
      continue;
    }

    slots.push({
      id: `${model}-${garment}-${index}`,
      modelType: model,
      garmentType: garment,
      ethnicity: slotMeta?.ethnicity || '',
      index,
      slotIndex: index,
      presetIndex: index - 1,
      ethnicityLabel,
      url: '',
      source: 'system',
      isEmpty: true,
      selectable: false,
    });
  }

  return slots;
}

function readTryOnAttributes(item = {}) {
  const attrs = item?.attributes && typeof item.attributes === 'object'
    ? item.attributes
    : {};
  return {
    modelType: attrs.modelType || item.modelType || '',
    garmentType: attrs.garmentType || item.garmentType || '',
    ethnicity: attrs.ethnicity || item.ethnicity || '',
    poseIndex: attrs.poseIndex ?? item.poseIndex,
  };
}

function resolveTryOnPresetApiUrl(item = {}) {
  const candidates = [item.url, item.src, item.displayUrl, item.viewUrl];
  for (const candidate of candidates) {
    const signed = uploadsPathFromSignedViewUrl(candidate);
    if (signed) return signed;
    const persisted = normalizePersistedImageUrl(candidate);
    if (persisted) return persisted;
    const raw = String(candidate || '').trim();
    if (raw.startsWith('/assets/model-try-on-presets/')) return raw;
  }
  return '';
}

/** 对齐后端 SystemModelStudioAlignment.toTryOnPresetView + 用户端 signed displayUrl */
export function mapSystemModelItemToTryOnPresetView(item) {
  if (!item) return null;
  const attrs = readTryOnAttributes(item);
  const apiId = String(item.id || '').trim();
  const legacyPresetId = String(item.legacyPresetId || '').trim();
  // 与后端 resolveEffectivePresetId 一致：无 legacy 时按 modelType-garmentType-poseIndex 合成
  const synthesizedId = buildLegacyTryOnPresetId(
    attrs.modelType,
    attrs.garmentType,
    attrs.poseIndex,
  );
  const presetId = legacyPresetId || synthesizedId || apiId;
  if (!presetId) return null;

  const poseIndex = Number(attrs.poseIndex);
  const apiUrl = resolveTryOnPresetApiUrl(item);
  const displayUrl = String(item.displayUrl || item.viewUrl || '').trim();

  const view = {
    id: presetId,
    modelType: attrs.modelType,
    garmentType: attrs.garmentType,
    ethnicity: attrs.ethnicity,
    url: apiUrl || displayUrl,
    displayUrl: item.displayUrl || '',
    viewUrl: item.viewUrl || '',
    assetRev: item.assetRev,
    source: 'system',
  };
  if (apiUrl) {
    view.apiUrl = apiUrl;
  }
  if (apiUrl && isTryOnSystemPresetAssetUrl(apiUrl)) {
    view.assetExt = readStaticPresetAssetExtFromUrl(apiUrl) || MODEL_TRY_ON_PRESET_DISK_EXT;
  }
  if (apiId) {
    view.apiId = apiId;
  }
  if (legacyPresetId) {
    view.legacyPresetId = legacyPresetId;
  } else if (synthesizedId) {
    view.legacyPresetId = synthesizedId;
  }
  if (Number.isFinite(poseIndex) && poseIndex > 0) {
    view.index = poseIndex;
  }
  return view;
}

export function mapPublishedItemsToTryOnPresets(items = []) {
  if (!Array.isArray(items)) return [];
  const views = items.map(mapSystemModelItemToTryOnPresetView).filter(Boolean);
  return sortTryOnPresetsByPoseIndex(views).slice(0, MODEL_TRY_ON_PRESETS_PER_COMBO);
}

export function buildStaticTryOnPresetFallback(modelType, garmentType) {
  return buildStaticTryOnPresets(modelType, garmentType);
}

function buildTryOnPresetsQuery({ modelType, garmentType, ethnicity } = {}) {
  const query = {
    featureCode: 'modelTryOn',
    modelType: String(modelType || '').trim(),
    garmentType: String(garmentType || '').trim(),
    page: 1,
    pageSize: 3,
  };
  const eth = String(ethnicity || '').trim();
  if (eth) {
    query.ethnicity = eth;
  }
  return query;
}

/**
 * 节点 12.1：系统模特选择器数据源 — 仅读 /api/system/models。
 * @returns {Promise<{ presets: Array, source: string }>}
 */
export async function fetchTryOnPresets({
  modelType,
  garmentType,
  ethnicity,
} = {}) {
  const model = String(modelType || '').trim();
  const garment = String(garmentType || '').trim();

  if (!model || !garment || !isGarmentTypeAllowedForModel(garment, model)) {
    return {
      presets: [],
      source: TRY_ON_PRESET_SOURCE.EMPTY,
    };
  }

  const response = await systemModelsAPI.list(buildTryOnPresetsQuery({
    modelType: model,
    garmentType: garment,
    ethnicity,
  }));
  const apiPresets = mapPublishedItemsToTryOnPresets(response?.items);
  return {
    presets: apiPresets,
    source: apiPresets.length > 0 ? TRY_ON_PRESET_SOURCE.API : TRY_ON_PRESET_SOURCE.EMPTY,
    staleCacheUsed: Boolean(response?.staleCacheUsed),
  };
}

/**
 * 节点 11.7 / 12.5 / 16.5：API 无数据或故障时 fallback 到 buildStaticTryOnPresets。
 * @returns {Promise<{ presets: Array, source: string, fallbackUsed: boolean }>}
 */
export async function fetchTryOnPresetsWithStaticFallback({
  modelType,
  garmentType,
  ethnicity,
} = {}) {
  const model = String(modelType || '').trim();
  const garment = String(garmentType || '').trim();
  const staticPresets = buildStaticTryOnPresetFallback(model, garment);

  if (!model || !garment || !isGarmentTypeAllowedForModel(garment, model)) {
    return {
      presets: [],
      source: TRY_ON_PRESET_SOURCE.EMPTY,
      fallbackUsed: false,
    };
  }

  try {
    const apiResult = await fetchTryOnPresets({ modelType: model, garmentType: garment, ethnicity });
    if (apiResult.presets.length > 0) {
      return {
        presets: apiResult.presets,
        source: TRY_ON_PRESET_SOURCE.API,
        fallbackUsed: Boolean(apiResult.staleCacheUsed),
      };
    }
  } catch {
    // API 不可用时走静态 fallback（节点 12.5 / 16.5）
  }

  return {
    presets: staticPresets,
    source: TRY_ON_PRESET_SOURCE.STATIC,
    fallbackUsed: true,
  };
}

export function findTryOnPresetInList(presets = [], presetId) {
  const id = String(presetId || '').trim();
  if (!id) return null;
  return presets.find((item) => {
    const candidates = [
      item?.apiId,
      item?.id,
      item?.legacyPresetId,
    ].map((value) => String(value || '').trim()).filter(Boolean);
    return candidates.includes(id);
  }) || null;
}

function readStaticPresetAssetExtFromUrl(url) {
  const path = String(url || '').trim().split('?')[0];
  const match = path.match(/\.(webp|png|jpe?g)$/i);
  if (!match) return '';
  const ext = match[1].toLowerCase();
  return ext === 'jpeg' ? 'jpg' : ext;
}

function normalizeTryOnPresetUrlForMatch(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  const persisted = normalizePersistedImageUrl(raw);
  if (persisted) return persisted;
  const fromSigned = uploadsPathFromSignedViewUrl(raw);
  if (fromSigned) return fromSigned;
  if (isTryOnSystemPresetAssetUrl(raw)) {
    return resolveTryOnPresetSubmitUrl(raw);
  }
  return raw;
}

/** 按持久化 URL 在 preset 列表中查找（用于 legacy selectedPresetUrl 还原） */
export function findTryOnPresetInListByUrl(presets = [], url) {
  const target = normalizeTryOnPresetUrlForMatch(url);
  if (!target) return null;
  return presets.find((preset) => {
    const candidates = [
      preset?.apiUrl,
      preset?.url,
      preset?.displayUrl,
      preset?.viewUrl,
    ].map(normalizeTryOnPresetUrlForMatch).filter(Boolean);
    return candidates.includes(target);
  }) || null;
}

/** 在当前 preset 列表或静态表中查找 */
export function resolveTryOnPresetSelection({
  presets = [],
  modelType,
  garmentType,
  presetId,
} = {}) {
  const id = String(presetId || '').trim();
  if (!id) return null;
  return findTryOnPresetInList(presets, id)
    || findTryOnPresetById(modelType, garmentType, id);
}

/**
 * 从工作流/任务参数还原系统模特（同步：须传入已加载的 presets 列表）。
 * 查找顺序：API id / legacy id → legacy URL（selectedPresetUrl）→ 静态表。
 */
export function resolveTryOnPresetFromParamsSync(
  modelType,
  garmentType,
  params = {},
  presets = [],
) {
  const presetId = String(params.selectedPresetId || '').trim();
  const savedUrl = String(params.selectedPresetUrl || '').trim();
  if (!presetId && !savedUrl) return null;

  if (presetId) {
    const byId = findTryOnPresetInList(presets, presetId)
      || findTryOnPresetById(modelType, garmentType, presetId);
    if (byId) return byId;
  }

  if (savedUrl) {
    const byUrl = findTryOnPresetInListByUrl(presets, savedUrl);
    if (byUrl) return byUrl;
    return {
      id: presetId,
      modelType,
      garmentType,
      url: savedUrl,
      source: 'saved',
    };
  }

  return null;
}

/**
 * 从工作流/任务参数还原系统模特（异步：拉取 API + 静态 fallback 后按 id / URL 还原）。
 */
export async function resolveTryOnPresetFromParams(
  modelType,
  garmentType,
  params = {},
  options = {},
) {
  const presetId = String(params.selectedPresetId || '').trim();
  if (!presetId && !params.selectedPresetUrl) return null;

  let presets = options.presets;
  if (!Array.isArray(presets)) {
    const ethnicity = String(params.ethnicity || params.modelEthnicity || '').trim();
    const fetched = await fetchTryOnPresetsWithStaticFallback({
      modelType,
      garmentType,
      ethnicity: ethnicity || undefined,
    });
    presets = fetched.presets;
  }
  return resolveTryOnPresetFromParamsSync(modelType, garmentType, params, presets);
}

export function resolveTryOnPresetCardDisplaySrc(preset) {
  if (!preset) return '';
  const signed = String(preset.displayUrl || preset.viewUrl || '').trim();
  if (signed) return signed;
  if (preset.source === 'static' || isTryOnSystemPresetAssetUrl(preset?.url)) {
    return blockBareManagedSystemModelImageSrc(preferTryOnPresetWebpUrl(preset.url || ''));
  }
  return blockBareManagedSystemModelImageSrc(preset.url || '');
}

export function shouldFetchTryOnPresetRemoteBlob(preset) {
  if (!preset) return false;
  if (preset.source === 'static') return false;
  if (String(preset.displayUrl || preset.viewUrl || '').trim()) return false;
  if (isTryOnSystemPresetAssetUrl(preset.url)) return false;
  return Boolean(String(preset.url || '').trim());
}
